import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Alert, AlertDescription } from '../components/ui/alert';
import { 
  ArrowLeft, 
  Check, 
  CreditCard, 
  Calendar,
  CheckCircle,
  XCircle,
  Loader2
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SubscriptionPage = () => {
  const { user, refreshUser } = useAuth();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [checkingPayment, setCheckingPayment] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState(null);

  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    if (sessionId) {
      pollPaymentStatus(sessionId);
    }
  }, [sessionId]);

  const pollPaymentStatus = async (sid, attempts = 0) => {
    if (attempts >= 5) {
      setPaymentStatus('timeout');
      return;
    }

    setCheckingPayment(true);
    try {
      const response = await axios.get(`${API_URL}/api/payments/status/${sid}`);
      
      if (response.data.payment_status === 'paid') {
        setPaymentStatus('success');
        await refreshUser();
      } else if (response.data.status === 'expired') {
        setPaymentStatus('expired');
      } else {
        setTimeout(() => pollPaymentStatus(sid, attempts + 1), 2000);
        return;
      }
    } catch (error) {
      console.error('Payment status check failed:', error);
      setPaymentStatus('error');
    } finally {
      setCheckingPayment(false);
    }
  };

  const handleCheckout = async (type, id) => {
    setLoading(true);
    try {
      const payload = type === 'bundle' 
        ? { bundle_id: id, origin_url: window.location.origin }
        : { plan_id: id, origin_url: window.location.origin };
      
      console.log('Starting checkout with payload:', payload);
      const response = await axios.post(`${API_URL}/api/payments/checkout`, payload);
      console.log('Checkout response:', response.data);
      
      if (response.data.url) {
        console.log('Redirecting to:', response.data.url);
        // Try multiple redirect methods
        try {
          window.location.href = response.data.url;
        } catch (e) {
          console.error('Direct redirect failed, trying window.open:', e);
          window.open(response.data.url, '_self');
        }
      } else {
        console.error('No URL in response:', response.data);
        alert('Payment setup failed - no checkout URL received. Please try again.');
      }
    } catch (error) {
      console.error('Checkout failed:', error);
      const errorMessage = error.response?.data?.detail || error.message || 'Unknown error';
      alert(`Checkout failed: ${errorMessage}. Please try again.`);
    } finally {
      setLoading(false);
    }
  };

  const subscriberFeatures = [
    'Access to Live Event Feeds',
    'Watch Live on TV (pairing code / AirPlay)',
    'Monthly Credits Included',
    'Discounted Event Gallery Pricing (photos & videos)',
    'Community access',
    'AI Training Assistant',
    'Free Training Video Library',
    'Marketplace access',
    'Critique history',
  ];

  const subscriptionPlans = [
    {
      id: 'monthly',
      name: 'Monthly',
      price: '$25',
      period: '/month',
      description: 'Full platform access',
      features: subscriberFeatures,
      popular: false
    },
    {
      id: 'annual',
      name: 'Annual',
      price: '$240',
      period: '/year',
      description: 'Save $60 (2 months free)',
      features: subscriberFeatures,
      popular: true
    }
  ];

  const creditBundles = [
    { id: 'credits20', name: '20 Credits', credits: 20, price: '$20', perCredit: '$1.00/credit' },
    { id: 'credits50', name: '50 Credits', credits: 50, price: '$45', perCredit: '$0.90/credit' },
    { id: 'credits100', name: '100 Credits', credits: 100, price: '$80', perCredit: '$0.80/credit' }
  ];

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-CA', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const isActiveSubscriber = user?.subscription_status === 'active' || (
    user?.subscription_status === 'trial' &&
    user?.subscription_end &&
    new Date(user.subscription_end) > new Date()
  );

  return (
    <div className="min-h-screen bg-background pb-24 md:pb-8">
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="mb-8">
          <Link to="/dashboard" className="text-muted-foreground hover:text-foreground inline-flex items-center gap-2 mb-4">
            <ArrowLeft className="w-4 h-4" />
            Back to Dashboard
          </Link>
          <h1 className="font-display font-bold text-3xl uppercase tracking-tight">
            Subscription & Elevated Credits
          </h1>
        </div>

        {/* Payment Status */}
        {sessionId && (
          <Card className="mb-8">
            <CardContent className="py-6">
              {checkingPayment ? (
                <div className="flex items-center justify-center gap-3">
                  <Loader2 className="w-6 h-6 animate-spin text-primary" />
                  <span>Verifying payment...</span>
                </div>
              ) : paymentStatus === 'success' ? (
                <Alert className="bg-secondary/10 border-secondary">
                  <CheckCircle className="w-5 h-5 text-secondary" />
                  <AlertDescription className="text-secondary font-semibold">
                    Payment successful! Your account has been updated.
                  </AlertDescription>
                </Alert>
              ) : paymentStatus === 'expired' || paymentStatus === 'error' ? (
                <Alert variant="destructive">
                  <XCircle className="w-5 h-5" />
                  <AlertDescription>
                    Payment could not be verified. Please try again or contact support.
                  </AlertDescription>
                </Alert>
              ) : null}
            </CardContent>
          </Card>
        )}

        {/* Current Status */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle className="font-display text-lg uppercase">Current Status</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-3 gap-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                  <CreditCard className="w-6 h-6 text-primary" />
                </div>
                <div>
                  <p className="font-display font-bold text-2xl">{user?.credits || 0}</p>
                  <p className="text-sm text-muted-foreground">Elevated Credits Available</p>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-full bg-secondary/10 flex items-center justify-center">
                  <Calendar className="w-6 h-6 text-secondary" />
                </div>
                <div>
                  <Badge
                    variant={
                      user?.subscription_status === 'active' ? 'default' : 
                      user?.subscription_status === 'trial' && user?.subscription_end && new Date(user.subscription_end) < new Date() ? 'destructive' :
                      'secondary'
                    }
                    className={user?.subscription_status === 'active' ? 'bg-secondary' : ''}
                  >
                    {user?.subscription_status === 'trial' && user?.subscription_end && new Date(user.subscription_end) < new Date() 
                      ? 'Trial Ended' 
                      : (user?.subscription_status || 'None')}
                  </Badge>
                  <p className="text-sm text-muted-foreground mt-1">
                    {user?.subscription_plan ? `${user.subscription_plan} plan` : 'No active plan'}
                  </p>
                </div>
              </div>
              {user?.subscription_end && (
                <div>
                  <p className="text-sm text-muted-foreground">
                    {user?.subscription_status === 'trial' && new Date(user.subscription_end) < new Date()
                      ? `Trial ended: ${formatDate(user.subscription_end)}`
                      : user?.subscription_status === 'trial' 
                        ? `Trial ends: ${formatDate(user.subscription_end)}`
                        : `Renews: ${formatDate(user.subscription_end)}`}
                  </p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Credit Bundles — hidden until user has an active subscription. Credits
            can only be topped up AFTER subscribing ($25 first, credits later). */}
        {isActiveSubscriber ? (
          <div className="mb-12">
            <h2 className="font-display font-bold text-2xl uppercase tracking-wide mb-2">
              Buy Elevated Credits
            </h2>
            <div className="grid md:grid-cols-3 gap-4 mt-6">
              {creditBundles.map(bundle => (
                <Card key={bundle.id} className="relative">
                  <CardContent className="p-6 text-center">
                    <p className="font-display font-bold text-4xl text-primary">{bundle.credits}</p>
                    <p className="text-muted-foreground uppercase tracking-wide text-sm mb-2">Elevated Credits</p>
                    <p className="font-bold text-2xl mb-1">{bundle.price} CAD</p>
                    <p className="text-sm text-muted-foreground mb-4">{bundle.perCredit}</p>
                    <Button
                      onClick={() => handleCheckout('bundle', bundle.id)}
                      disabled={loading}
                      className="w-full rounded-full"
                      data-testid={`buy-${bundle.id}-btn`}
                    >
                      {loading ? 'Processing...' : 'Buy Now'}
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        ) : (
          <div className="mb-12 rounded-xl border-2 border-dashed border-muted-foreground/30 bg-muted/30 p-6 text-center">
            <p className="text-sm text-muted-foreground">
              <b>Elevated Credits</b> unlock after you subscribe. Pick a plan below to get started — then come back here to top up as needed.
            </p>
          </div>
        )}

        {/* Subscription Plans */}
        <div>
          <h2 className="font-display font-bold text-2xl uppercase tracking-wide mb-2">
            Subscription Plans
          </h2>
          <p className="text-muted-foreground mb-6">
            Membership gets you access. Elevated Credits are purchased separately.
          </p>
          <div className="grid md:grid-cols-2 gap-6">
            {subscriptionPlans.map(plan => (
              <Card key={plan.id} className={`relative ${plan.popular ? 'border-primary border-2' : ''}`}>
                {plan.popular && (
                  <div className="absolute top-0 right-0 bg-primary text-primary-foreground px-4 py-1 text-xs font-bold uppercase rounded-bl-lg">
                    Best Value
                  </div>
                )}
                <CardContent className="p-8">
                  <h3 className="font-display font-bold text-xl uppercase tracking-wide">{plan.name}</h3>
                  <div className="mt-4 flex items-baseline">
                    <span className="font-display font-black text-5xl">{plan.price}</span>
                    <span className="text-muted-foreground ml-2">{plan.period}</span>
                  </div>
                  <p className="text-muted-foreground mt-2">{plan.description}</p>
                  <ul className="mt-6 space-y-3">
                    {plan.features.map((feature, i) => (
                      <li key={i} className="flex items-center gap-3">
                        <Check className="w-5 h-5 text-secondary" />
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                  <Button
                    onClick={() => handleCheckout('plan', plan.id)}
                    disabled={loading || user?.subscription_status === 'active'}
                    className={`w-full mt-8 rounded-full py-6 font-bold uppercase ${
                      plan.popular ? 'bg-primary hover:bg-primary/90' : ''
                    }`}
                    data-testid={`subscribe-${plan.id}-btn`}
                  >
                    {user?.subscription_status === 'active' 
                      ? 'Currently Subscribed' 
                      : loading ? 'Processing...' : 'Subscribe'}
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SubscriptionPage;
